import { FixedSizeList, FixedSizeListProps, ListChildComponentProps } from "react-window";
import React, { ComponentProps, createContext, CSSProperties, FC, use, useMemo } from "react";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import "./VirtualTable.css";
import { clsx } from "clsx";

// Create a generic context with type parameter T
export interface StickyListContextType<T> {
    rowData: T[];
    columnDefinitions?: ColumnDefinition<T>[];
    rowDefinition?: RowDefinition<T>;
    listProps: Omit<FixedSizeListProps, "children" | "innerElementType" | "itemCount" | "itemData">;
}

// Create a context with a default type
export const StickyListContext = createContext<StickyListContextType<unknown>>({
    rowData: [],
    columnDefinitions: [],
    listProps: undefined,
});

export type ColumnDefinition<T> = {
    key: string;
    width: number | "*";
    ellipsis?: boolean;
    className?: string;
    headers?: React.ReactNode[];
    render: (item: T) => React.ReactNode | string | number;

    // sortable?: boolean;
    // sortFunc?: (a: T, b: T) => number;
    // sortDirection?: "asc" | "desc" | null;
    // alignment?: "left" | "right" | "center";
};

type RowDefinition<T> = {
    // onSelect?: (v: T, index: number) => void;
    // isRowSelectable?: (v: T, index: number) => boolean;

    onRowClick?: (v: T, index: number) => void;
    isRowClickable?: (v: T, index: number) => boolean;

    getRowClassNames?: (v: T, index: number) => string;
};

const StickyList = <T,>(contextData: StickyListContextType<T>) => {
    return (
        <StickyListContext value={contextData}>
            <FixedSizeList
                {...contextData.listProps}
                overscanCount={Math.max(30, contextData.listProps?.overscanCount ?? 0)}
                innerElementType={innerElementType}
                itemCount={contextData?.rowData?.length ?? 0}
                itemData={contextData.rowDefinition}
                className={clsx("VirtualTable", contextData.listProps?.className)}
            >
                {ItemRow}
            </FixedSizeList>
        </StickyListContext>
    );
};

const getWidthCellProps = (width: number | "*", className?: string): { className: string; style?: CSSProperties } => {
    return Number.isFinite(width)
        ? { className: `size-fixed ${className ?? ""}`, style: { width } }
        : { className: `size-shrink ${className ?? ""}` };
};

const StickyRow: FC<{ index: number }> = ({ index }) => {
    const { columnDefinitions } = use(StickyListContext);
    return (
        <thead className="vt-sticky" style={{ top: 25 * index, left: 0, width: "100%", height: 25 }}>
            <tr>
                {columnDefinitions.map(column => (
                    <th key={`header:${index}:${column.key}`} {...getWidthCellProps(column.width, "vt-cell")}>
                        {column.headers?.[index]}
                    </th>
                ))}
            </tr>
        </thead>
    );
};

const innerElementType: FixedSizeListProps["innerElementType"] = ({ ref, children, ...rest }) => {
    return (
        <StickyListContext.Consumer>
            {({ columnDefinitions, listProps }) => {
                const headersAmount = Math.max(...columnDefinitions.map(c => c.headers?.length ?? 0));
                return (
                    <table ref={ref as never} {...rest}>
                        {headersAmount ? <StickyRow index={0} /> : undefined}

                        <tbody
                            style={{
                                position: "absolute",
                                width: "100%",
                                height: `calc(100% - ${headersAmount * listProps.itemSize}px)`,
                            }}
                        >
                            {children}
                        </tbody>
                    </table>
                );
            }}
        </StickyListContext.Consumer>
    );
};

const ItemRow = <T,>({ index, style, data }: ListChildComponentProps<RowDefinition<T>>) => {
    const { columnDefinitions, rowData } = use<StickyListContextType<T>>(StickyListContext);
    const item = rowData[index];
    const canClick = data.isRowClickable?.(item, index);
    return (
        <tr
            style={{ ...style }}
            onClick={canClick ? () => data.onRowClick?.(item, index) : undefined}
            className={`vt-row ${data.getRowClassNames?.(item, index) ?? ""}`}
        >
            {columnDefinitions.map((column, i) => (
                <td
                    key={`${column.key}:${i}:${index}`}
                    {...getWidthCellProps(
                        column.width,
                        clsx(`vt-cell`, column.className, { "vt-cell--ellipsis": column.ellipsis })
                    )}
                >
                    {column.render(item)}
                </td>
            ))}
        </tr>
    );
};

export const VirtualTable = <T,>({
    data,
    itemHeight,
    columnDefinitions,
    rowDefinition,
    autosizerProps,
    listProps = {},
}: {
    data: T[];
    itemHeight: number;
    columnDefinitions?: ColumnDefinition<T>[];
    rowDefinition: RowDefinition<T>;
    autosizerProps?: ComponentProps<typeof AutoSizer>;
    listProps?: Partial<StickyListContextType<T>["listProps"]>;
}) => {
    return (
        <AutoSizer {...autosizerProps}>
            {({ width, height }: Size) => (
                <StickyList
                    rowData={data}
                    listProps={{ ...listProps, ...{ width, height, itemSize: itemHeight } }}
                    columnDefinitions={columnDefinitions}
                    rowDefinition={rowDefinition}
                />
            )}
        </AutoSizer>
    );
};
